import React from 'react';
import BlogLayout from '../../components/BlogLayout';

const RoofClawVsAdminROI = () => {
    return (
        <BlogLayout
            title="RoofClaw vs Hiring an Admin Assistant: The Real ROI Math"
            date="Mar 26, 2026"
            readTime={6}
            heroImage="/images/blog/roofclaw-vs-admin-roi-math-hero.png"
            description="A full-time admin costs $75,000+ per year fully-loaded. RoofClaw costs $5,000 once. Here's the arithmetic most roofing owners skip."
        >
            <p className="lead text-xl text-[#F0EFF4] font-medium">
                You can hire an admin assistant for $18-22 an hour. Someone to answer phones, chase down paperwork, follow up on estimates, coordinate the crew schedule, handle the supplier calls. Real help. Real overhead.
            </p>
            <p>
                Most roofing owners who are growing past $1.5M are either doing all of this themselves (which is costing them more than $22 an hour) or they're thinking about hiring someone to take it off their plate.
            </p>
            <p>
                That's a reasonable instinct. But before you post the job listing, run the actual numbers.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16">What an Admin Actually Costs</h2>
            <p>
                Wages are the number everyone quotes. They're also the smallest part.
            </p>
            <p>
                A full-time admin at $20/hour is $41,600 a year in wages. But you're not paying $41,600. Here's what you're actually paying:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>Annual wages: $41,600</li>
                <li>Employer payroll taxes (CPP/EI or FICA, roughly 8%): $3,328</li>
                <li>Workers' compensation: $800-$1,500 depending on your province or state</li>
                <li>Health benefits (if you offer them): $4,000-$8,000</li>
                <li>Paid vacation (2 weeks minimum): $1,600</li>
                <li>Paid sick days (5 days, industry average): $800</li>
                <li>Onboarding and training time (your time plus theirs): $2,000-$4,000 in lost productivity</li>
                <li>Turnover cost (average tenure for admin roles in SMB is 1.5-2 years, and replacement typically runs 50% of annual salary): $20,800 per year amortized</li>
            </ul>
            <p className="font-bold text-[#F0EFF4]">
                Realistic annual fully-loaded cost: $75,000-$82,000.
            </p>
            <p>
                That's not a scare tactic. That's what HR departments have calculated for decades. The wage is the floor, not the ceiling.
            </p>
            <p>
                And that's before you account for the things an admin can't do.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16">What an Admin Can't Do</h2>
            <p>
                A good admin will help. They'll answer calls, file paperwork, remind you of follow-ups, keep the schedule organized. If you hire well, you'll feel the difference.
            </p>
            <p>
                But an admin is a human with a shift. They work 9-5. They have bad days. They get sick. They leave. When they leave, they take the institutional knowledge with them: who your key clients are, what they prefer, what happened on that complicated job two years ago.
            </p>
            <p>
                <strong className="text-[#F0EFF4]">An admin doesn't cross systems.</strong> They work in whatever tab you gave them access to. They can't look at your CRM, your QuickBooks, your email, and your scheduling app at the same time and surface a pattern that's been costing you money for six months.
            </p>
            <p>
                <strong className="text-[#F0EFF4]">An admin doesn't compound.</strong> They learn your preferences, yes. But they don't analyze why close rates drop in November, or notice that every job with a particular supplier has a 40% callback rate, or flag that three of your last five one-star reviews came from the same crew assignment pattern.
            </p>
            <p>
                An admin is a capable human doing admin work. That's the ceiling.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16">What RoofClaw Actually Costs</h2>
            <p>
                RoofClaw is $5,000 USD. Once.
            </p>
            <p>
                That's the pre-consult call, the full build, up to three API integrations connected to your existing tools, and a three-hour training session where your agent goes live with you in the room. Plus ongoing access to Winning Wednesday (the weekly group support call) and a dedicated Telegram line to your agent, always on.
            </p>
            <p>
                No monthly subscription. No per-seat pricing. No annual renewal that quietly doubles.
            </p>
            <p>
                You own the hardware. You own the agent. Nobody has a back door to your system, by design.
            </p>
            <p>
                Over two years, compare:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-6">
                <li><strong className="text-[#F0EFF4]">Admin assistant:</strong> $150,000-$164,000 (fully-loaded, two years)</li>
                <li><strong className="text-[#F0EFF4]">RoofClaw:</strong> $5,000 (hardware plus setup, one-time)</li>
            </ul>
            <p>
                If RoofClaw saves you four hours a week, which is conservative given that most clients report six to ten, you've recovered the investment before the first month is over.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16">The Real Question</h2>
            <p>
                The honest comparison isn't "admin vs RoofClaw." It's "what do you actually need?"
            </p>
            <p>
                If you need someone to meet clients at the door, run physical errands, manage in-person office logistics, or represent your company at appointments, hire a human. RoofClaw doesn't shake hands.
            </p>
            <p>
                If you need someone to hold context across every part of your business, remember every client conversation, never miss a follow-up, never call in sick, work at midnight during storm season without overtime, and get better at your specific operation over time rather than eventually leaving for a better offer, that's not a job description a human admin can fill. That's a different category of help.
            </p>
            <p>
                Most roofing owners who are scaling past $2M don't just need an admin. They need a chief of staff. Someone who sees the whole board, not just the inbox.
            </p>
            <p className="font-bold text-[#7B61FF] text-xl">
                RoofClaw was built for that role.
            </p>

            <h2 className="text-3xl font-heading font-bold mb-6 mt-16">The Math Doesn't Lie</h2>
            <p>
                You can hire a solid admin for $20/hour. It'll cost you $75,000 or more fully-loaded before the first year is out.
            </p>
            <p>
                Or you can put $5,000 into a dedicated AI employee that runs on your hardware, connects to every tool you use, and works every hour of every day without benefits, vacation, turnover, or training overhead.
            </p>
            <p>
                The ROI math isn't close. That's not spin. It's arithmetic.
            </p>
            <p>
                The question is whether you're ready to trust a machine with the work.
            </p>
            <p>
                Most roofers who've made that call wish they'd done it sooner.
            </p>
        </BlogLayout>
    );
};

export default RoofClawVsAdminROI;
